import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Clock, ChevronDown } from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────

/** Convert 24-hour "HH:mm" to 12-hour display string, e.g. "9:00 AM" */
function to12Hour(time24: string): string {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

/** Generate all 15-minute time slots from 00:00 to 23:45 in 24-hour "HH:mm" format */
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      );
    }
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

// ── Types ────────────────────────────────────────────────────────────

interface TimePickerProps {
  value: string; // "HH:mm" 24-hour format (e.g. "09:00", "13:30")
  onChange: (value: string) => void;
  className?: string;
}

// ── Component ────────────────────────────────────────────────────────

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Auto-scroll to selected time when dropdown opens
  useEffect(() => {
    if (isOpen && selectedRef.current && listRef.current) {
      const list = listRef.current;
      const item = selectedRef.current;

      // Scroll so the selected item is roughly centered in the visible area
      const scrollTarget =
        item.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2;
      list.scrollTop = Math.max(0, scrollTarget);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleSelect = useCallback(
    (slot: string) => {
      onChange(slot);
      setIsOpen(false);
    },
    [onChange]
  );

  const displayValue = useMemo(() => to12Hour(value), [value]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`inline-flex items-center gap-1.5 bg-white border rounded-lg px-3 py-1.5 text-sm transition-all cursor-pointer select-none whitespace-nowrap ${
          isOpen
            ? "border-emerald-600 ring-2 ring-emerald-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <Clock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        <span className="text-slate-700 font-medium">
          {displayValue || "Select"}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 w-[140px] max-h-[200px] overflow-y-auto bg-white rounded-xl shadow-lg border border-slate-200 py-1"
          style={{ scrollbarWidth: "thin" }}
        >
          {TIME_SLOTS.map((slot) => {
            const isSelected = slot === value;
            return (
              <button
                key={slot}
                ref={isSelected ? selectedRef : undefined}
                type="button"
                onClick={() => handleSelect(slot)}
                className={`w-full text-left px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-emerald-50 text-emerald-700 font-medium"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {to12Hour(slot)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimePicker;
